/**
 * Очередь «Без исполнителя» на рабочем столе исполнителя (ТЗ §8.3).
 * Разметка строки — `.decision-row` из блока «Ждут вашего решения» макета,
 * действие — `POST /api/cases/:id/take` («Взять в работу»): по ТЗ §2 исполнитель
 * может забрать только дело, у которого исполнителя нет.
 */
import { useTakeCase } from '@/api/hooks';
import type { CaseListItem } from '@/api/types';
import { deadlineClass } from '@/lib/deadlines';
import { caseWord } from '@/lib/format';
import { categoryLabel } from '@/lib/categories';
import { useToast } from '@/components/Toast';
import { caseFlags, rowKeyDown } from './CaseRow';

interface QueueRowProps {
    item: CaseListItem;
    canTake: boolean;
    onOpen: (caseId: string) => void;
}

function QueueRow({ item, canTake, onOpen }: QueueRowProps) {
    const toast = useToast();
    const take = useTakeCase(item.case_id);
    const open = () => onOpen(item.case_id);

    const takeCase = () => {
        take.mutate(undefined, {
            onSuccess: () => toast(`Дело ${item.number} взято в работу`),
            onError: (error) => toast(error.message),
        });
    };

    return (
        <div
            className="decision-row"
            data-case={item.case_id}
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(event) => rowKeyDown(event, open)}
        >
            <span className="dr-reason">без исполнителя</span>
            <span className="dr-id">{item.number}</span>
            <span className="dr-title">
                {item.title}
                {caseFlags(item)}
            </span>
            <span className="dr-exec">{categoryLabel(item.primary_category) || 'без категории'}</span>
            <span className={`dr-date ${deadlineClass(item.days_left)}`.trim()}>{item.deadline_text}</span>
            {canTake ? (
                <button
                    type="button"
                    className="act-btn primary"
                    disabled={take.isPending}
                    onClick={(event) => {
                        event.stopPropagation();
                        takeCase();
                    }}
                >
                    {take.isPending ? 'Берём…' : 'Взять в работу'}
                </button>
            ) : null}
        </div>
    );
}

export interface UnassignedQueueProps {
    rows: readonly CaseListItem[];
    count: number;
    /** Кнопка «Взять в работу» есть только у исполнителя. */
    canTake: boolean;
    onOpen: (caseId: string) => void;
}

export function UnassignedQueue({ rows, count, canTake, onOpen }: UnassignedQueueProps) {
    // В шапке настоящий счётчик очереди, строк сервер отдаёт не больше двадцати.
    const rest = count - rows.length;
    return (
        <div className="stat-block" style={{ marginBottom: 18 }}>
            <div className="block-header">
                <span>
                    📥 Без исполнителя{' '}
                    <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f' }}>
                        {count > 0 ? `· ${count} ${caseWord(count)}` : ''}
                    </span>
                </span>
                <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f', fontSize: 14 }}>
                    очередь назначения
                </span>
            </div>
            <div className="block-body">
                {rows.length === 0 ? (
                    <div className="detail-empty">Свободных дел нет — дела без исполнителя появятся здесь после разбора.</div>
                ) : (
                    <>
                        {rows.map((item) => (
                            <QueueRow key={item.case_id} item={item} canTake={canTake} onOpen={onOpen} />
                        ))}
                        {rest > 0 ? (
                            <div className="case-more">
                                и ещё {rest} {caseWord(rest)}
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}

export default UnassignedQueue;
